/* ═══════════════════════════════════════════════════════════════
   PiARIS — SubView Hero
   Generic mini-banner used on every product sub-view (dashboard,
   web audit, doc audit, etc.). Looks up the product gradient and
   shows: icon + PiARIS lockup + product name + sub-view title.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { PRODUCT_GRADIENTS } from "./gradients";
import { ProductLockup } from "./ProductLockup";

var E = React.createElement;

/** SubViewHero — mini gradient banner.
    Props:
      product:  "watchtower" | "docmaya" | "a11ylab" | "deltarecon"
      title:    string — sub-view name (e.g. "Dashboard", "Web Audit")
      subtitle: string — optional secondary description
      right:    React node — optional content rendered on the right (actions, badges) */
function SubViewHero(p) {
  var g = PRODUCT_GRADIENTS[p.product];
  if (!g) return null;
  return E("section", {
    role: "region",
    "aria-label": g.name + " " + (p.title || ""),
    className: "subViewHero",
    style: {
      borderRadius: 14,
      padding: "16px 24px",
      marginBottom: 16,
      background: g.hero,
      color: "#FFFFFF",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
      boxShadow: "0 4px 24px " + g.glow,
      position: "relative",
      overflow: "hidden"
    }
  },
    /* Decorative glow blob */
    E("div", {
      "aria-hidden": "true",
      style: {
        position: "absolute",
        top: -40,
        right: -40,
        width: 160,
        height: 160,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)",
        pointerEvents: "none"
      }
    }),
    /* Left: icon + lockup + product + title */
    E("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        position: "relative",
        zIndex: 1,
        minWidth: 0,
        flex: 1
      }
    },
      E("div", {
        "aria-hidden": "true",
        style: {
          width: 44,
          height: 44,
          borderRadius: 10,
          background: "rgba(255,255,255,0.15)",
          border: "1.5px solid rgba(255,255,255,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          flexShrink: 0
        }
      }, g.icon),
      E("div", { style: { minWidth: 0, flex: 1 } },
        E("div", {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 2,
            flexWrap: "wrap"
          }
        },
          E(ProductLockup, { size: "tiny", white: true }),
          E("span", { "aria-hidden": "true", style: { color: "rgba(255,255,255,0.5)", fontSize: 11 } }, "/"),
          E("span", {
            style: {
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(255,255,255,0.9)",
              textTransform: "uppercase",
              letterSpacing: ".06em"
            }
          }, g.name)
        ),
        E("h2", {
          style: {
            fontSize: 18,
            fontWeight: 800,
            color: "#FFFFFF",
            margin: 0,
            lineHeight: 1.25,
            letterSpacing: "-0.2px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }
        }, p.title || ""),
        p.subtitle ? E("p", {
          style: {
            fontSize: 11,
            color: "rgba(255,255,255,0.78)",
            margin: "2px 0 0",
            lineHeight: 1.4
          }
        }, p.subtitle) : null
      )
    ),
    /* Right: optional actions */
    p.right ? E("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        position: "relative",
        zIndex: 1,
        flexShrink: 0
      }
    }, p.right) : null
  );
}

export { SubViewHero };
